import { create } from 'zustand';
import type { UploadQueueItem, UploadQueueState } from '../types';
import {
  generateId,
  isLargeFile,
  ACTIVE_STATUSES,
  DUPLICATE_NOTICE_PREFIX,
  DUPLICATE_NOTICE_TIMEOUT,
} from '../constants';

/**
 * Generate a unique key for an upload queue item based on scope, fileName, and fileSize.
 * This is used to prevent duplicate items in the queue.
 */
function getItemKey(item: { scope: string; fileName: string; fileSize: number }): string {
  return `${item.scope}:${item.fileName}:${item.fileSize}`;
}

/**
 * Upload Queue Store
 *
 * Manages the state of all file uploads across the application.
 * Uses scope-based grouping to support multiple independent queues.
 *
 * Note: We don't persist to localStorage because File objects can't be
 * serialized, so uploads can't be resumed after page reload anyway.
 */
export const useUploadQueueStore = create<UploadQueueState>()(
  (set, get) => ({
      items: [],
      abortControllers: new Map(),
      isPaused: false,

      addItems: (newItems) => {
        const { items: existingItems } = get();

        // Audit M5a: dedupe ONLY against active items. Completed/errored
        // items linger until manually cleared — deduping against them made
        // re-adding the same file a silent no-op forever.
        const activeByKey = new Map(
          existingItems
            .filter((item) => ACTIVE_STATUSES.includes(item.status))
            .map((item) => [getItemKey(item), item] as const)
        );

        const uniqueNewItems: typeof newItems = [];
        const rejectedDuplicates: string[] = [];
        const duplicateOfIds: string[] = [];
        for (const item of newItems) {
          const existing = activeByKey.get(getItemKey(item));
          if (existing) {
            rejectedDuplicates.push(item.fileName);
            duplicateOfIds.push(existing.id);
          } else {
            uniqueNewItems.push(item);
          }
        }

        const itemsWithDefaults: UploadQueueItem[] = uniqueNewItems.map((item) => ({
          ...item,
          id: generateId(),
          status: 'pending' as const,
          progress: 0,
          retryCount: 0,
          addedAt: Date.now(),
        }));

        if (itemsWithDefaults.length > 0) {
          set((state) => ({
            items: [...state.items, ...itemsWithDefaults],
          }));
        }

        // Surface each rejection loudly on the matching active item (same
        // transient-notice mechanism as the auto-retry countdown message).
        for (const existingId of duplicateOfIds) {
          const notice = `${DUPLICATE_NOTICE_PREFIX} — this file is already in the queue`;
          get().updateItem(existingId, { error: notice });
          setTimeout(() => {
            const current = get().items.find((i) => i.id === existingId);
            // Only clear if our notice is still showing — don't clobber a
            // real error (or retry countdown) that replaced it.
            if (current?.error === notice) {
              get().updateItem(existingId, { error: undefined });
            }
          }, DUPLICATE_NOTICE_TIMEOUT);
        }

        // The store never validates: it has no access to the server's
        // published caps. `useUploadQueue.addFiles` is the entry point that
        // does, and it is the only caller (#84).
        return { added: itemsWithDefaults, rejectedDuplicates, rejectedInvalid: [] };
      },

      removeItem: (id) => {
        const { abortControllers } = get();
        // Cancel if in progress
        const controller = abortControllers.get(id);
        if (controller) {
          controller.abort();
          abortControllers.delete(id);
        }

        set((state) => ({
          items: state.items.filter((item) => item.id !== id),
          abortControllers: new Map(abortControllers),
        }));
      },

      updateItem: (id, updates) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.id === id ? { ...item, ...updates } : item
          ),
        }));
      },

      setAbortController: (id, controller) => {
        const { abortControllers } = get();
        abortControllers.set(id, controller);
        set({ abortControllers: new Map(abortControllers) });
      },

      removeAbortController: (id) => {
        const { abortControllers } = get();
        abortControllers.delete(id);
        set({ abortControllers: new Map(abortControllers) });
      },

      clearCompleted: (scope) => {
        set((state) => ({
          items: state.items.filter((item) => {
            if (scope && item.scope !== scope) return true;
            return item.status !== 'completed';
          }),
        }));
      },

      setIsPaused: (isPaused) => {
        set({ isPaused });
      },

      setQuota: (usedBytes, maxBytes) => {
        set({ usedBytes, maxBytes });
      },

      // Selectors
      getItemsByScope: (scope) => {
        return get().items.filter((item) => item.scope === scope);
      },

      getNextPendingItem: () => {
        const { items, isPaused } = get();
        if (isPaused) return null;

        // Get all pending items. Audit H3: an item whose retryAfter is in
        // the future is backing off after a retryable failure — it is not
        // eligible yet. Without this gate every store change re-dispatched
        // it immediately and all retries burned back-to-back.
        const now = Date.now();
        const pendingItems = items.filter(
          (item) =>
            item.status === 'pending' &&
            item.file &&
            (item.retryAfter === undefined || now >= item.retryAfter)
        );

        if (pendingItems.length === 0) return null;

        // Sort by file size (smallest first), then by addedAt (oldest first)
        pendingItems.sort((a, b) => {
          if (a.fileSize !== b.fileSize) {
            return a.fileSize - b.fileSize;
          }
          return a.addedAt - b.addedAt;
        });

        return pendingItems[0] ?? null;
      },

      getUploadingItems: () => {
        return get().items.filter((item) => item.status === 'uploading');
      },

      hasActiveUploads: (scope) => {
        const { items } = get();

        return items.some((item) => {
          if (scope && item.scope !== scope) return false;
          return ACTIVE_STATUSES.includes(item.status);
        });
      },

      getQueuedBytes: () => {
        return get()
          .items.filter((item) => ACTIVE_STATUSES.includes(item.status))
          .reduce((sum, item) => sum + (item.fileSize || 0), 0);
      },

      wouldExceedQuota: (fileSizeBytes) => {
        const { usedBytes, maxBytes } = get();
        if (usedBytes === undefined || maxBytes === undefined) return false;
        return usedBytes + get().getQueuedBytes() + fileSizeBytes > maxBytes;
      },
    })
);

/**
 * Helper to check if we can start a new upload based on concurrency rules
 */
export function canStartUpload(
  uploadingItems: UploadQueueItem[],
  newItem: UploadQueueItem,
  maxConcurrent: number = 5,
  maxLargeConcurrent: number = 2
): boolean {
  // Check total concurrent limit
  if (uploadingItems.length >= maxConcurrent) {
    return false;
  }

  // If the new item is large, check large file limit
  if (isLargeFile(newItem.fileSize)) {
    const largeUploading = uploadingItems.filter((item) =>
      isLargeFile(item.fileSize)
    );
    if (largeUploading.length >= maxLargeConcurrent) {
      return false;
    }
  }

  return true;
}
